// ตั้งค่าหน้าต่างรับแตะบัตรของแต่ละเวลาละหมาด
//
// นักเรียนแตะตอนไหนก็ได้ในช่วงนี้ — ขาขึ้นไปละหมาด หรือหลังละหมาดเสร็จ
// (ของเดิมที่เช็คลงกระดาษก็เช็คหลังละหมาด) ระบบจึงไม่ต้องรู้ว่าตั้งแถวกันตอนไหน
// มีแค่สองค่าต่อหนึ่งเวลาละหมาด: เปิดรับก่อนกี่นาที และปิดรับหลังกี่นาที
//
// ปิดรับหลังต้องยาวพอให้ละหมาดจบ เพราะส่วนใหญ่แตะกันตอนเสร็จแล้ว
// ตั้งสั้นไปเท่ากับปิดหน้าต่างตั้งแต่ยังไม่ทันเริ่มละหมาด
//
// ถ้าโรงเรียนละหมาดช้ากว่าเวลาปฏิทินเป็นประจำ ไม่ต้องมีช่องแยก แค่เพิ่มค่าปิดรับหลัง
// หน้านี้โชว์เวลานาฬิกาจริงข้างค่าที่กรอกเสมอ เพราะตัวเลข "45 นาที" ไม่ได้บอกอะไร
// จนกว่าจะเห็นว่ามันแปลว่ารับแตะบัตรถึงสามทุ่มหนึ่งนาที
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';

const API = process.env.REACT_APP_API || 'http://localhost:3002/api';

const clock = (v) =>
  (v ? new Date(v).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) : '-');

const FIELDS = [
  { key: 'before_min', label: 'เปิดรับแตะก่อนเวลาละหมาด', unit: 'นาที', min: 0, max: 120 },
  { key: 'after_min', label: 'ปิดรับแตะหลังเวลาละหมาด', unit: 'นาที', min: 10, max: 180 },
];

export default function PrayerSettings() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];

  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [photo, setPhoto] = useState(null);
  const [photoDraft, setPhotoDraft] = useState({ retention_days: '365', purge_auto: true });
  const [purging, setPurging] = useState(false);

  const [face, setFace] = useState(null);
  const [facing, setFacing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`${API}/prayer/windows`);
      setRows(data);
      setDraft(Object.fromEntries(data.map((r) => [
        r.prayer,
        { before_min: String(r.before_min), after_min: String(r.after_min) },
      ])));
    } catch (error) {
      Swal.fire('อ่านค่าไม่สำเร็จ', error?.response?.data?.message || 'ลองใหม่อีกครั้ง', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // นโยบายการเก็บรูปอ่านแยก เพราะต้องเดินดูไฟล์บนดิสก์ซึ่งช้ากว่าการอ่านตาราง
  // ให้หน้าต่างเวลาขึ้นก่อนแล้วส่วนนี้ตามมา ดีกว่าค้างทั้งหน้ารอ
  const loadPhoto = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`${API}/prayer/photo-policy`);
      setPhoto(data);
      setPhotoDraft({ retention_days: String(data.retention_days), purge_auto: data.purge_auto });
    } catch {
      setPhoto(null);   // ดูไม่ได้ก็ไม่ควรทำให้หน้าตั้งค่าหน้าต่างเวลาใช้ไม่ได้ไปด้วย
    }
  }, []);

  // สถานะตัวจดจำใบหน้า — อ่านแยกด้วยเหตุผลเดียวกับนโยบายรูป
  // ต้องยิงข้ามไปถามบริการอีกโปรเซส ซึ่งอาจไม่อยู่ และนั่นต้องไม่ทำให้
  // หน้าตั้งค่าหน้าต่างเวลาใช้ไม่ได้ไปด้วย
  const loadFace = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`${API}/prayer/face/health`);
      setFace(data);
    } catch {
      setFace(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPhoto(); }, [loadPhoto]);
  useEffect(() => { loadFace(); }, [loadFace]);

  /**
   * เปลี่ยนโหมดการเช็คชื่อด้วยใบหน้า
   *
   * ยืนยันก่อนเสมอตอนเปิด live เพราะเป็นจุดที่ระบบเริ่ม "ตัดสินใจแทนคน"
   * ความผิดพลาดแบบเดียวที่แก้ยากคือเช็คให้คนที่ไม่ได้มา ซึ่งไม่มีใครรู้ว่าเกิด
   */
  const changeFaceMode = async (mode) => {
    if (mode === 'live') {
      const confirm = await Swal.fire({
        title: 'เปิดให้ใบหน้าเช็คชื่อจริง?',
        html:
          'ตั้งแต่นี้ระบบจะ<b>ลงผล "มา" ให้เอง</b>เมื่อกล้องจำหน้าได้<br>' +
          '<span class="text-secondary">บัตรยังใช้ได้เหมือนเดิม และคนที่เครื่องไม่กล้าทายต้องแตะบัตร<br>' +
          'ดูรายงานเทียบกับบัตรจากโหมดทดสอบให้แน่ใจก่อน</span>',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'เปิดใช้จริง',
        cancelButtonText: 'ยกเลิก',
      });
      if (!confirm.isConfirmed) return;
    }

    setFacing(true);
    try {
      const { data } = await axiosInstance.put(`${API}/prayer/face/mode`, { mode });
      setFace(data);
      Swal.fire('เปลี่ยนโหมดแล้ว', 'มีผลกับทุกเครื่องในรอบถัดไป', 'success');
    } catch (error) {
      Swal.fire('เปลี่ยนโหมดไม่สำเร็จ', error?.response?.data?.message || 'ลองใหม่อีกครั้ง', 'error');
    } finally {
      setFacing(false);
    }
  };

  const savePhotoPolicy = async () => {
    const days = Number(photoDraft.retention_days);
    if (!Number.isInteger(days) || days < 30 || days > 730) {
      Swal.fire('ค่าไม่ถูกต้อง', 'อายุการเก็บรูปต้องอยู่ระหว่าง 30 ถึง 730 วัน', 'warning');
      return;
    }
    try {
      const { data } = await axiosInstance.put(`${API}/prayer/photo-policy`, {
        retention_days: days,
        purge_auto: photoDraft.purge_auto,
      });
      setPhoto(data);
      Swal.fire('บันทึกแล้ว', 'มีผลกับการลบรอบถัดไป', 'success');
    } catch (error) {
      Swal.fire('บันทึกไม่สำเร็จ', error?.response?.data?.message || 'ลองใหม่อีกครั้ง', 'error');
    }
  };

  // ลบเองตอนขึ้นปีการศึกษาใหม่ ไม่ต้องรอให้ครบอายุพอดี
  // ยืนยันก่อนเสมอเพราะย้อนกลับไม่ได้ และบอกให้ชัดว่าลบเฉพาะรูป ผลการเช็คชื่อยังอยู่
  const purgeNow = async () => {
    const confirm = await Swal.fire({
      title: 'ลบรูปที่พ้นอายุการเก็บ?',
      html:
        `ลบรูปที่เก่ากว่า <b>${photoDraft.retention_days} วัน</b> ตามค่าที่ตั้งไว้<br>` +
        '<span class="text-secondary">ผลการเช็คชื่อยังอยู่ครบ ลบเฉพาะรูป และย้อนกลับไม่ได้</span>',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ลบเลย',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545',
    });
    if (!confirm.isConfirmed) return;

    setPurging(true);
    try {
      const { data } = await axiosInstance.post(`${API}/prayer/photos/purge`);
      await Swal.fire(
        data.files > 0 ? 'ลบแล้ว' : 'ไม่มีรูปที่ต้องลบ',
        data.files > 0
          ? `ลบ ${data.files.toLocaleString('th-TH')} รูป (${data.human}) ของวันที่ ${data.from} ถึง ${data.to}`
          : 'ยังไม่มีรูปไหนเก่าเกินอายุการเก็บ',
        data.files > 0 ? 'success' : 'info'
      );
      loadPhoto();
    } catch (error) {
      Swal.fire('ลบไม่สำเร็จ', error?.response?.data?.message || 'ลองใหม่อีกครั้ง', 'error');
    } finally {
      setPurging(false);
    }
  };

  const dirty = rows.some((r) => {
    const d = draft[r.prayer];
    return d && FIELDS.some((f) => Number(d[f.key]) !== r[f.key]);
  });

  const setField = (prayer, key, value) =>
    setDraft((prev) => ({ ...prev, [prayer]: { ...prev[prayer], [key]: value } }));

  // ค่าที่กรอกแปลว่ารับแตะบัตรกี่โมงถึงกี่โมง — คำนวณสดขณะพิมพ์
  // ไม่ต้องกดบันทึกก่อนถึงจะเห็นผล เพราะการต้องบันทึกเพื่อดูผลจะทำให้คนลองค่าน้อยลง
  const previewFor = (row) => {
    const d = draft[row.prayer];
    if (!d) return null;
    const nums = FIELDS.map((f) => Number(d[f.key]));
    if (nums.some((n) => Number.isNaN(n))) return null;
    const [before, after] = nums;
    const base = new Date(row.prayer_at).getTime();
    return {
      opens: new Date(base - before * 60000),
      closes: new Date(base + after * 60000),
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.put(`${API}/prayer/windows`, {
        entries: rows.map((r) => ({
          prayer: r.prayer,
          before_min: Number(draft[r.prayer].before_min),
          after_min: Number(draft[r.prayer].after_min),
        })),
      });
      setRows(data);
      setDraft(Object.fromEntries(data.map((r) => [
        r.prayer,
        { before_min: String(r.before_min), after_min: String(r.after_min) },
      ])));
      Swal.fire('บันทึกแล้ว', 'มีผลกับรอบถัดไปทันที ไม่ต้องรีสตาร์ทระบบ', 'success');
    } catch (error) {
      Swal.fire('บันทึกไม่สำเร็จ', error?.response?.data?.message || 'ลองใหม่อีกครั้ง', 'error');
    } finally {
      setSaving(false);
    }
  };

  const applyToAll = (prayer) => {
    const source = draft[prayer];
    if (!source) return;
    setDraft((prev) => Object.fromEntries(
      Object.keys(prev).map((k) => [k, { ...source }])
    ));
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="mb-3">
          <h4 className="main-title mb-0">ตั้งค่าการเช็คชื่อละหมาด</h4>
          <div className="fs-xs text-secondary">
            กำหนดว่าแต่ละเวลารับแตะบัตรตั้งแต่กี่โมงถึงกี่โมง
          </div>
        </div>

        <Alert variant="light" className="fs-sm">
          <div className="fw-semibold mb-1">ค่านี้ไม่ใช่เวลาละหมาด</div>
          เวลาละหมาดที่ถูกต้องตามปฏิทินแก้ที่หน้า <b>เวลาละหมาด</b> ส่วนหน้านี้บอกแค่ว่า
          เปิดรับแตะบัตรกว้างแค่ไหนรอบเวลานั้น นักเรียนแตะได้ทั้งขาขึ้นไปละหมาด
          และหลังละหมาดเสร็จ แยกกันเพื่อไม่ให้เวลาละหมาดที่แสดงให้นักเรียนดูเพี้ยนตามไปด้วย
        </Alert>

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : (
          <>
            <Card className="card-one mb-3">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">หน้าต่างรับแตะบัตร</Card.Title>
              </Card.Header>
              <Card.Body>
                {rows.map((row) => {
                  const preview = previewFor(row);
                  return (
                    <div key={row.prayer} className="pb-3 mb-3 border-bottom">
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <span className="fw-semibold fs-5">{row.prayer_label}</span>
                        <span className="fs-xs text-secondary">
                          เวลาละหมาดวันนี้ {clock(row.prayer_at)}
                        </span>
                        <Button
                          size="sm"
                          variant="link"
                          className="ms-auto p-0 fs-xs"
                          onClick={() => applyToAll(row.prayer)}
                        >
                          ใช้ค่านี้กับทุกเวลา
                        </Button>
                      </div>

                      <Row className="g-2">
                        {FIELDS.map((f) => (
                          <Col xs={12} md={6} key={f.key}>
                            <Form.Label className="fs-xs text-secondary mb-1">
                              {f.label} ({f.unit})
                            </Form.Label>
                            <Form.Control
                              type="number"
                              inputMode="numeric"
                              min={f.min}
                              max={f.max}
                              value={draft[row.prayer]?.[f.key] ?? ''}
                              onChange={(e) => setField(row.prayer, f.key, e.target.value)}
                            />
                          </Col>
                        ))}
                      </Row>

                      {preview && (
                        <div className="fs-sm mt-2">
                          รับแตะบัตร <b className="text-primary">{clock(preview.opens)} – {clock(preview.closes)}</b>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="d-flex justify-content-end gap-2">
                  <Button variant="secondary" disabled={!dirty || saving} onClick={load}>ย้อนกลับ</Button>
                  <Button variant="primary" disabled={!dirty || saving} onClick={save}>
                    {saving && <Spinner size="sm" animation="border" className="me-2" />}
                    บันทึก
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {photo && (
              <Card className="card-one mb-3">
                <Card.Header>
                  <Card.Title as="h6" className="mb-0">รูปที่ถ่ายตอนแตะบัตร</Card.Title>
                </Card.Header>
                <Card.Body>
                  <div className="fs-sm text-secondary mb-3">
                    รูปเป็นข้อมูลส่วนบุคคลของนักเรียน เก็บเท่าที่จำเป็นแล้วลบ
                    ระบบจะแจ้งผู้ดูแลทุกครั้งที่ลบ ไม่ลบเงียบๆ
                  </div>

                  <Row className="g-2 mb-3">
                    {[
                      { label: 'รูปทั้งหมด', value: photo.usage.files.toLocaleString('th-TH') },
                      { label: 'พื้นที่ที่ใช้', value: photo.usage.human },
                      { label: 'เก่าสุด', value: photo.usage.oldest || '-' },
                      { label: 'ล่าสุด', value: photo.usage.newest || '-' },
                    ].map((box) => (
                      <Col xs={6} md={3} key={box.label}>
                        <div className="border rounded p-2 h-100">
                          <div className="fw-bold">{box.value}</div>
                          <div className="fs-xs text-secondary">{box.label}</div>
                        </div>
                      </Col>
                    ))}
                  </Row>

                  <Row className="g-2 align-items-end">
                    <Col xs={12} md={4}>
                      <Form.Label className="fs-xs text-secondary mb-1">เก็บย้อนหลัง (วัน)</Form.Label>
                      <Form.Control
                        type="number"
                        inputMode="numeric"
                        min={30}
                        max={730}
                        value={photoDraft.retention_days}
                        onChange={(e) => setPhotoDraft((d) => ({ ...d, retention_days: e.target.value }))}
                      />
                      <div className="fs-xs text-secondary mt-1">365 = หนึ่งปีการศึกษา</div>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Check
                        type="switch"
                        id="prayer-photo-auto"
                        label="ลบอัตโนมัติเมื่อพ้นอายุ"
                        checked={photoDraft.purge_auto}
                        onChange={(e) => setPhotoDraft((d) => ({ ...d, purge_auto: e.target.checked }))}
                      />
                      <div className="fs-xs text-secondary mt-1">
                        ปิดไว้ชั่วคราวได้ตอนที่กำลังตรวจสอบเรื่องใดเรื่องหนึ่ง
                      </div>
                    </Col>
                    <Col xs={12} md={4} className="text-md-end">
                      <Button variant="primary" className="me-2" onClick={savePhotoPolicy}>บันทึก</Button>
                      <Button variant="outline-danger" onClick={purgeNow} disabled={purging}>
                        {purging && <Spinner size="sm" animation="border" className="me-2" />}
                        ลบตอนนี้
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* การเช็คชื่อด้วยใบหน้า
                ลำดับที่ตั้งใจให้เดินคือ ปิด -> ทดสอบ -> ใช้จริง และห้ามข้ามขั้นกลาง
                เหตุผลอยู่ที่ prayerController.updateFaceMode (ฝั่ง server บังคับไว้ด้วย
                ไม่ได้พึ่งปุ่มบนหน้าจอ เพราะยิง endpoint ตรงได้) */}
            {face && (
              <Card className="card-one mb-3">
                <Card.Header>
                  <Card.Title as="h6" className="mb-0">เช็คชื่อด้วยใบหน้า</Card.Title>
                </Card.Header>
                <Card.Body>
                  {!face.configured ? (
                    <div className="text-secondary fs-sm">
                      <i className="ri-error-warning-line me-1"></i>
                      {face.message}
                    </div>
                  ) : (
                    <>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {[
                          { mode: 'off', label: 'ปิด', variant: 'secondary',
                            hint: 'ไม่รับภาพเลย ใช้บัตรอย่างเดียว' },
                          { mode: 'shadow', label: 'ทดสอบ (ไม่เช็คชื่อให้)', variant: 'info',
                            hint: 'ทายแล้วจดไว้เทียบกับบัตร นักเรียนยังต้องแตะบัตร' },
                          { mode: 'live', label: 'ใช้จริง', variant: 'success',
                            hint: 'จำหน้าได้แล้วลงผล "มา" ให้เอง บัตรยังเป็นทางสำรอง' },
                        ].map((opt) => (
                          <Button
                            key={opt.mode}
                            size="sm"
                            variant={face.mode === opt.mode ? opt.variant : `outline-${opt.variant}`}
                            disabled={facing || face.mode === opt.mode}
                            onClick={() => changeFaceMode(opt.mode)}
                            title={opt.hint}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>

                      {face.ready ? (
                        <div className="fs-xs text-secondary">
                          คลังใบหน้า {Number(face.identities || 0).toLocaleString('th-TH')} คน
                          {' '}({Number(face.vectors || 0).toLocaleString('th-TH')} รูป)
                          {' · '}โมเดล {face.model}
                          {' · '}เกณฑ์ {face.threshold} นำที่สอง {face.margin}
                        </div>
                      ) : (
                        <div className="fs-xs text-danger">{face.message || 'บริการจดจำใบหน้าไม่ตอบ'}</div>
                      )}

                      <div className="fs-xs text-secondary mt-2">
                        เกณฑ์ตั้งที่ตัวบริการ ไม่ใช่ที่หน้านี้ — แก้แล้วต้องเริ่มบริการใหม่
                        {' '}เพื่อให้ตัวเลขที่วัดไว้กับที่ใช้จริงเป็นชุดเดียวกันเสมอ
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            )}

            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">ค่าที่ใช้อยู่ตอนนี้</Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table className="mb-0" size="sm">
                    <thead>
                      <tr>
                        <th>เวลาละหมาด</th>
                        <th>เวลาจริง</th>
                        <th>รับแตะบัตร</th>
                        <th>แก้ล่าสุดโดย</th>
                      </tr>
                    </thead>
                    <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {rows.map((r) => (
                        <tr key={r.prayer}>
                          <td>{r.prayer_label}</td>
                          <td>{clock(r.prayer_at)}</td>
                          <td>{clock(r.opens_at)} – {clock(r.closes_at)}</td>
                          <td className="text-secondary fs-xs">{r.updated_by_name || 'ค่าเริ่มต้น'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        )}

        <Footer />
      </div>
    </>
  );
}
